/* eslint-disable */
import React, { createContext, useState } from "react";
import { Box } from "@mui/material";
// @ts-ignore
import { StriimButton } from "@striim/striim-ui";
// @ts-ignore
import { StriimMessageBox, StriimTypography } from "@striim/striim-ui-v2";
// @ts-ignore
import { sourceFormConfig } from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/source-form-info.js";
// @ts-ignore
import { getStepConfigs } from "app/components/appwizard/component-wizards/wizards/source/database-reader/base-database-reader";
// @ts-ignore

import { SourceConfigState, SourceMode } from "./source-configurator/source-configurator.types";
import VerticalStepper from "./source-configurator/vertical-stepper";

import SourceConfigForm from "./source-configurator/pages/source-form-config/source-form-config";
import SelectSchemaStepPage from "./source-configurator/pages/select-schemas/select-schemas";
import ValidateTablesPage from "./source-configurator/pages/validate-tables/validate-tables";
import SelectTablesWrapperPage from "./source-configurator/pages/select-tables/select-tables";
import ValidateConnectionStep from "./source-configurator/pages/validate-connection/validate-connection";
import SelectPdbStepPage from "./source-configurator/pages/select-pdb/select-pdb";
import { buildCancelledResponse } from "../utils/hitl-cancel";

export interface SourceConfiguratorToolProps {
    args: { sourceType: string; mode: SourceMode; targetComponentName?: string };
    respond?: (result: string) => void;
}

const cardSx = {
    border: "1px solid",
    borderColor: "greyscale.100",
    borderRadius: 2,
    p: 2,
    mt: 1,
    backgroundColor: "background.paper",
    width: "100%",

    "& .MuiStepContent-last": {
        padding: "0 !important",
        margin: "0 !important"
    }
};

const gradientSx = {
    background:
        "linear-gradient(132deg, rgba(243, 23, 121, 0.80) 0.9%, rgba(121, 48, 217, 0.80) 66.21%, rgba(17, 0, 0, 0.80) 127.52%)",
    color: "white",
    "&:disabled": { color: "white", opacity: 0.5 }
};

export const SourceConfiguratorTool: React.FC<SourceConfiguratorToolProps> = ({ args, respond }) => {
    const sourceType = args.sourceType?.toUpperCase() ?? "";
    const mode = args.mode;

    // ── Guard: Automated mode ──────────────────────────────────────────────
    if (mode === "Automated") {
        return (
            <Box sx={cardSx}>
                <Box mb={2}>
                    <StriimTypography variant="h2" sx={{ mb: 1, color: "primary.700" }}>
                        Configure Source
                    </StriimTypography>
                </Box>
                <Box mb={2}>
                    <StriimMessageBox
                        type="INFO"
                        text="Automated Pipelines are coming soon. Please re-run and pick Initial Load or Change Data Capture."
                        customCardStyles={{ width: "100%" }}
                    />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <StriimButton
                        variant="secondary"
                        size="small"
                        onClick={() => respond?.(JSON.stringify({ status: "automated-not-supported" }))}
                    >
                        OK
                    </StriimButton>
                </Box>
            </Box>
        );
    }

    // ── Guard: unknown sourceType ──────────────────────────────────────────
    if (!sourceType || !sourceFormConfig[sourceType]) {
        return (
            <Box sx={cardSx}>
                <Box mb={2}>
                    <StriimTypography variant="h2" sx={{ color: "primary.700" }}>
                        Configure Source
                    </StriimTypography>
                </Box>
                <Box mb={2}>
                    <StriimMessageBox
                        type="ERROR"
                        text={`Unknown source type: "${args.sourceType}". Please re-run with a supported adapter (e.g. ORACLE, POSTGRES, MYSQL).`}
                        customCardStyles={{ width: "100%" }}
                    />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <StriimButton
                        variant="secondary"
                        size="small"
                        onClick={() =>
                            respond?.(JSON.stringify({ status: "unknown-source-type", sourceType: args.sourceType }))
                        }
                    >
                        OK
                    </StriimButton>
                </Box>
            </Box>
        );
    }

    // ── Normal path ────────────────────────────────────────────────────────
    return (
        <SourceConfiguratorStepper
            sourceType={sourceType}
            mode={mode}
            targetComponentName={args.targetComponentName ?? null}
            respond={respond}
        />
    );
};

// Separate inner component so guards above don't run hooks conditionally.

interface contextState extends SourceConfigState {
    setState: Function;
}
export const wizardcontext = createContext<contextState>({
    sourceType: null,
    mode: "Initial Load",
    targetComponentName: null,
    connectionParams: {},
    schemas: [],
    selectedSchemas: [],
    discoveredTables: [],
    selectedTables: [],
    pdbList: [],
    selectedPdb: null,
    setState: () => {}
});

const SourceConfiguratorStepper: React.FC<{
    sourceType: string;
    mode: SourceMode;
    targetComponentName: string | null;
    respond?: (result: string) => void;
}> = ({ sourceType, mode, targetComponentName, respond }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [done, setDone] = useState(false);
    const [state, setState] = useState<SourceConfigState>({
        sourceType,
        mode,
        targetComponentName,
        connectionParams: {},
        schemas: [],
        selectedSchemas: [],
        discoveredTables: [],
        selectedTables: [],
        pdbList: [],
        selectedPdb: null
    });

    const handleChange = (patch: Partial<SourceConfigState>) => setState(prev => ({ ...prev, ...patch }));
    const handleCancel = () => respond?.(buildCancelledResponse());
    const handleNext = () => setActiveStep(s => s + 1);
    const handleBack = () =>
        setActiveStep(s => {
            let prev = s - 1;
            while (prev > 0 && allSteps[prev]?.transient) prev--;
            return prev;
        });

    const handleComplete = (latestSelectedTables?: string[]) => {
        setDone(true);
        const selectedTables = latestSelectedTables ?? state.selectedTables;
        respond?.(
            JSON.stringify({
                status: "Source configured",
                sourceType: state.sourceType,
                mode: state.mode,
                connectionParams: state.connectionParams,
                selectedTables,
                message: "Proceed with the target configuration if mentioned    "
            })
        );
    };

    const stepProps = { state, onChange: handleChange, onCancel: handleCancel };

    const stepContentMap: Record<string, { content: React.ReactNode; transient?: boolean }> = {
        "source-info": { content: <SourceConfigForm {...stepProps} onNext={handleNext} /> },
        "select-pluggable-database": {
            content: <SelectPdbStepPage {...stepProps} onNext={handleNext} onBack={handleBack} />,
            transient: state.isPdb === false
        },
        "validate-source": {
            content: <ValidateConnectionStep {...stepProps} onNext={handleNext} onBack={handleBack} />,
            transient: true
        },
        "select-namespace": {
            content: <SelectSchemaStepPage {...stepProps} onNext={handleNext} onBack={handleBack} />
        },
        "validate-namespace": {
            content: <ValidateTablesPage {...stepProps} onNext={handleNext} onBack={handleBack} />,
            transient: true
        },
        "select-tables": {
            content: <SelectTablesWrapperPage {...stepProps} onNext={handleComplete} onBack={handleBack} />
        }
    };

    const allSteps = getStepConfigs(sourceType)
        .filter(config => config.stepName in stepContentMap)
        .map(config => ({
            label: config.title,
            content: stepContentMap[config.stepName].content,
            transient: stepContentMap[config.stepName].transient ?? false,
            noIndent: config.stepName === "select-tables"
        }));

    const steps = allSteps;

    return (
        <wizardcontext.Provider value={{ ...state, setState }}>
            <Box sx={cardSx}>
                <Box sx={{ mb: 1 }}>
                    <StriimTypography variant="h2" sx={{ color: "primary.700" }}>
                        Configure {sourceType} Source
                    </StriimTypography>
                </Box>

                {done ? (
                    <Box>
                        <StriimMessageBox
                            type="SUCCESS"
                            text={`Source configured — ${state.selectedTables.length} table${
                                state.selectedTables.length !== 1 ? "s" : ""
                            } selected.`}
                            customCardStyles={{ width: "100%" }}
                        />
                    </Box>
                ) : (
                    <VerticalStepper activeStep={activeStep} steps={steps} />
                )}
            </Box>
        </wizardcontext.Provider>
    );
};
